import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Check, ChevronRight, Loader2 } from "lucide-react";
import {
  PrivacyPolicyFullText,
  TermsOfServiceFullText,
  DisclaimerFullText,
} from "@/components/full-legal-document-popup";

type LegalAcceptanceModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // Reason: fired only after the user scrolls through and agrees to every document
  onComplete: () => void;
  isSubmitting?: boolean;
};

// Order requested: Privacy Policy > Terms of Service > Disclaimer
const steps = [
  { key: "privacy", title: "Privacy Policy", Content: PrivacyPolicyFullText },
  { key: "terms", title: "Terms of Service", Content: TermsOfServiceFullText },
  { key: "disclaimer", title: "Disclaimer", Content: DisclaimerFullText },
] as const;

// Buffer (px) so sub-pixel rounding never blocks the Agree button at the true bottom.
const SCROLL_BUFFER = 24;

export function LegalAcceptanceModal({
  open,
  onOpenChange,
  onComplete,
  isSubmitting = false,
}: LegalAcceptanceModalProps) {
  const [stepIndex, setStepIndex] = useState(0);
  const [reachedEnd, setReachedEnd] = useState(false);
  const [progress, setProgress] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  const isLastStep = stepIndex === steps.length - 1;
  const current = steps[stepIndex];

  // Reset to the first document whenever the modal is (re)opened.
  useEffect(() => {
    if (open) setStepIndex(0);
  }, [open]);

  // On each step change: jump to top, and pre-clear the gate if the document
  // is short enough that it needs no scrolling.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = 0;
    const noScrollNeeded = el.scrollHeight - el.clientHeight < SCROLL_BUFFER;
    setReachedEnd(noScrollNeeded);
    setProgress(noScrollNeeded ? 100 : 0);
  }, [stepIndex, open]);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const max = el.scrollHeight - el.clientHeight;
    const pct = max <= 0 ? 100 : Math.min(100, Math.round((el.scrollTop / max) * 100));
    setProgress(pct);
    if (max - el.scrollTop <= SCROLL_BUFFER) setReachedEnd(true);
  };

  const handleAgree = () => {
    if (isLastStep) {
      onComplete();
    } else {
      setStepIndex((i) => i + 1);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        // Reason: keep the guided flow intact — don't lose progress to a stray outside click
        onInteractOutside={(e) => e.preventDefault()}
        className="sm:max-w-[800px] h-[85vh] md:h-[90vh] flex flex-col bg-white dark:bg-gray-800 p-0 rounded-lg overflow-hidden"
      >
        <DialogHeader className="px-6 pt-5 pb-4 border-b border-gray-200 dark:border-gray-700 space-y-3">
          {/* Breadcrumb progress across the three documents */}
          <nav aria-label="Progress" className="flex items-center gap-1.5 text-sm">
            {steps.map((s, i) => {
              const done = i < stepIndex;
              const isCurrent = i === stepIndex;
              return (
                <React.Fragment key={s.key}>
                  {i > 0 && <ChevronRight className="h-4 w-4 text-gray-400 dark:text-gray-500" />}
                  <span
                    aria-current={isCurrent ? "step" : undefined}
                    className={`flex items-center gap-1 font-medium ${
                      isCurrent
                        ? "text-blue-600 dark:text-blue-400"
                        : done
                        ? "text-green-600 dark:text-green-400"
                        : "text-gray-400 dark:text-gray-500"
                    }`}
                  >
                    {done && <Check className="h-4 w-4" />}
                    {s.title}
                  </span>
                </React.Fragment>
              );
            })}
            <span className="ml-auto text-xs font-normal text-gray-500 dark:text-gray-400">
              Step {stepIndex + 1} of {steps.length}
            </span>
          </nav>

          <div>
            <DialogTitle className="text-xl font-semibold text-gray-800 dark:text-white">
              {current.title}
            </DialogTitle>
            <DialogDescription className="text-sm text-gray-500 dark:text-gray-400">
              Please scroll to the bottom to continue.
            </DialogDescription>
          </div>

          {/* In-document scroll progress bar */}
          <div className="h-1 w-full rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden">
            <div
              className="h-full bg-blue-600 dark:bg-blue-500 transition-[width] duration-150"
              style={{ width: `${progress}%` }}
            />
          </div>
        </DialogHeader>

        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="relative flex-1 overflow-y-auto p-6 text-sm text-gray-700 dark:text-gray-300"
        >
          <div data-document-type={current.key}>
            <current.Content />
          </div>
        </div>

        <div className="flex items-center justify-between gap-4 p-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          {reachedEnd ? (
            <span className="text-sm text-green-600 dark:text-green-400 flex items-center gap-1">
              <Check className="h-4 w-4" /> You've reached the end
            </span>
          ) : (
            <span className="text-sm text-gray-500 dark:text-gray-400">
              Scroll to the bottom to continue
            </span>
          )}

          <Button
            type="button"
            onClick={handleAgree}
            disabled={!reachedEnd || isSubmitting}
            className="bg-[#0A51A8] hover:bg-[#084088] dark:bg-blue-700 dark:hover:bg-blue-800"
          >
            {isSubmitting && isLastStep ? (
              <span className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" /> Creating account...
              </span>
            ) : isLastStep ? (
              "Agree & Create Account"
            ) : (
              "Agree & Continue"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
